import { Capacitor } from "@capacitor/core";
import { spErrorTags } from "@eventr/common";
import { PAYWALL_RESULT, RevenueCatUI } from "@revenuecat/purchases-capacitor-ui";
import { CHOOSE_PLAN_URL } from "consts";
import { useDeviceInfo } from "feat/appState/deviceInfo";
import { bs } from "state/bound.store";
import { useOverlayStore } from "state/overlayState/overlay.store";
import { compareBuildVersion, isTeamForWeb, openBrowser } from "utils";
import { fakePurchase } from "./fakePurchase";

/** The first build carrying a paywall. */
const PAYWALL_MIN_BUILD = "2.5.0";

/** Whether `buildVersion` can present the paywall. Empty until the device reports it. */
export function paywallSupported(buildVersion: string): boolean {
  return compareBuildVersion(buildVersion, PAYWALL_MIN_BUILD) === true;
}

/** The same question outside React. */
export function paywallAvailable(): boolean {
  return paywallSupported(useDeviceInfo.getState().version.buildVersion);
}

/** Whether the paywall left the organisation on a different plan. */
export type UpgradeResult = "bought" | "dismissed";

/**
 * Presents the subscription paywall. Waits under a spinner for the new plan to reach the
 * app, only when something was bought or restored. The hosted browser build opens the
 * pricing page; a local web build gets the fake-purchase dialog, which RevenueCat has no
 * paywall for.
 */
export async function presentUpgradePaywall(): Promise<UpgradeResult> {
  // Before any await: a browser only honours window.open inside the press that caused it.
  if (isTeamForWeb) {
    openBrowser(CHOOSE_PLAN_URL);
    return "dismissed";
  }

  if (Capacitor.getPlatform() === "web") {
    void fakePurchase();
    return "dismissed";
  }

  try {
    const { result } = await RevenueCatUI.presentPaywall({ displayCloseButton: true });
    if (result !== PAYWALL_RESULT.PURCHASED && result !== PAYWALL_RESULT.RESTORED) return "dismissed";
  } catch (error) {
    spErrorTags("Error showing paywall", ["SUBSCRIPTION"], error);
    return "dismissed";
  }

  try {
    useOverlayStore.getState().setLoading(true);
    await bs().syncSubscription({ untilClaimsChange: true });
  } catch (error) {
    spErrorTags("Error syncing subscription", ["SUBSCRIPTION"], error);
  } finally {
    useOverlayStore.getState().setLoading(false);
  }
  return "bought";
}
